/**
 * EarniWalletModal.styles.js — Styled-components pour EarniWalletModal.
 *
 * Règles DA respectées :
 * §20  — Zéro littéral couleur/font/radius : tout importé depuis theme.js.
 * §12 ter — Zéro px en dur (sauf bordures fines 1-2 px) ; rem/em/clamp/% partout.
 * §12 quinquies — Bords arrondis : cards/modals 1rem, boutons 0.625rem.
 * §18 ter — Au moins 1 breakpoint responsive par écran DA.
 * §12 undecies — Casse grammaticale, pas d'uppercase forcé CSS.
 * §14  — Zéro @mui/@material-ui.
 */
import styled from 'styled-components';
import {COLORS, FONTS, RADII, SHADOW, SPACE, media, FONT_SIZE, FONT_WEIGHT} from '../../ui/theme';

/* ═══════════════════════════════════════════════════════════════════════════════
   LAYOUT MODAL
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Conteneur principal de la modal — remplace ModalCard pour EarniWalletModal
 * afin de gérer une largeur adaptée et une min-height cohérente.
 * Compose ModalShell (overlay + a11y) ; cette card est passée en children.
 */
export const WalletModalCard = styled.div`
  width: clamp(20rem, 90vw, 28rem);
  min-height: 24rem;
  background: ${COLORS.white};
  border-radius: ${RADII.card};
  border: 0.0625rem solid ${COLORS.line};
  box-shadow: ${SHADOW.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${media.sm`
    width: 100vw;
    min-height: 60vh;
    border-radius: ${RADII.card} ${RADII.card} 0 0;
    align-self: flex-end;
  `}
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   HEADER
   ═══════════════════════════════════════════════════════════════════════════ */

/** Header violet avec titre centré et croix cerclée (§12 sexies). */
export const WalletHeader = styled.div`
  position: relative;
  background: ${COLORS.accent};
  color: ${COLORS.white};
  padding: ${SPACE.lg} 3.5rem;
  text-align: center;
  flex-shrink: 0;
`;

export const WalletHeaderTitle = styled.h3`
  margin: 0;
  font-family: ${FONTS.head};
  font-size: ${FONT_SIZE.base};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.2;
  color: ${COLORS.white};
`;

/** Sous-titre optionnel du header (état courant en langage utilisateur). */
export const WalletHeaderSub = styled.p`
  margin: ${SPACE.xs} 0 0;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.onAccentTextSoft};
  line-height: 1.4;
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   BODY / FOOTER
   ═══════════════════════════════════════════════════════════════════════════ */

export const WalletBody = styled.div`
  flex: 1;
  padding: ${SPACE.xl};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${SPACE.lg};

  ${media.sm`
    padding: ${SPACE.lg};
    gap: ${SPACE.md};
  `}
`;

export const WalletFooter = styled.div`
  padding: ${SPACE.md} ${SPACE.xl};
  border-top: 0.0625rem solid ${COLORS.line};
  text-align: center;
  flex-shrink: 0;
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   WALLET PICKER — grille 2×2 de boutons portefeuille
   ═══════════════════════════════════════════════════════════════════════════ */

export const WalletGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACE.md};

  ${media.sm`
    grid-template-columns: 1fr;
  `}
`;

export const WalletButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE.sm};
  padding: ${SPACE.lg} ${SPACE.md};
  border: 0.0625rem solid ${COLORS.line};
  border-radius: ${RADII.card};
  background: ${(p) => (p.disabled ? COLORS.subtle : COLORS.white)};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  transition: box-shadow 0.15s, border-color 0.15s;
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    box-shadow: ${SHADOW.hover};
    border-color: ${COLORS.accent};
  }

  &:focus-visible {
    outline: 0.125rem solid ${COLORS.accent};
    outline-offset: 0.125rem;
  }
`;

export const WalletIcon = styled.img`
  width: 2.5rem;
  height: 2.5rem;
  object-fit: contain;
  flex-shrink: 0;
`;

export const WalletName = styled.span`
  font-family: ${FONTS.body};
  font-size: 0.8125rem;
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${COLORS.ink};
  text-align: center;
`;

export const WalletConnectLabel = styled.span`
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.muted};
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   FORMULAIRES EMAIL
   ═══════════════════════════════════════════════════════════════════════════ */

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE.xs};
`;

export const FormLabel = styled.label`
  font-family: ${FONTS.body};
  font-size: 0.8125rem;
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${COLORS.ink};
`;

export const FormInput = styled.input`
  height: 2.5rem;
  padding: 0 ${SPACE.md};
  border: 0.0625rem solid ${(p) => (p.$hasError ? COLORS.danger : COLORS.line)};
  border-radius: ${RADII.button};
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.sm};
  color: ${COLORS.ink};
  background: ${COLORS.white};
  width: 100%;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 0.1875rem ${COLORS.accentSoft};
  }

  &::placeholder {
    color: ${COLORS.muted};
  }
`;

export const FormInputWrap = styled.div`
  position: relative;
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: ${SPACE.md};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: ${COLORS.muted};
  display: flex;
  align-items: center;
  padding: 0;

  &:hover {
    color: ${COLORS.ink};
  }
`;

export const InlineError = styled.p`
  margin: 0;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.danger};
  line-height: 1.4;
`;

export const InlineWarning = styled.p`
  margin: 0;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.gold};
  line-height: 1.4;
`;

/* Barre de force du mot de passe (4 segments) */
export const PasswordStrengthBar = styled.div`
  height: 0.25rem;
  border-radius: ${RADII.pill};
  background: ${COLORS.line};
  overflow: hidden;
  margin-top: ${SPACE.xs};
`;

export const PasswordStrengthFill = styled.div`
  height: 100%;
  border-radius: ${RADII.pill};
  width: ${(p) => p.$percent ?? 0}%;
  background: ${(p) =>
    p.$level === 0 ? COLORS.danger
    : p.$level === 1 ? COLORS.gold
    : p.$level === 2 ? COLORS.cta
    : COLORS.ctaHover};
  transition: width 0.2s, background 0.2s;
`;

export const PasswordStrengthLabel = styled.span`
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.muted};
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   BOUTONS PRIMAIRES / SECONDAIRES
   ═══════════════════════════════════════════════════════════════════════════ */

export const PrimaryBtn = styled.button`
  width: 100%;
  height: 2.75rem;
  border-radius: ${RADII.button};
  border: none;
  background: ${(p) => (p.disabled ? COLORS.subtle : COLORS.accent)};
  color: ${(p) => (p.disabled ? COLORS.muted : COLORS.white)};
  font-family: ${FONTS.body};
  font-size: 0.9375rem;
  font-weight: ${FONT_WEIGHT.bold};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACE.sm};

  &:hover:not(:disabled) {
    background: ${COLORS.accentHover};
  }

  &:focus-visible {
    outline: 0.125rem solid ${COLORS.accent};
    outline-offset: 0.125rem;
  }
`;

export const SecondaryBtn = styled.button`
  width: 100%;
  height: 2.5rem;
  border-radius: ${RADII.button};
  border: 0.0625rem solid ${COLORS.line};
  background: transparent;
  color: ${COLORS.ink};
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.sm};
  font-weight: ${FONT_WEIGHT.medium};
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: ${COLORS.accent};
    color: ${COLORS.accent};
  }
`;

export const DangerBtn = styled.button`
  border: none;
  background: ${COLORS.danger};
  color: ${COLORS.white};
  border-radius: ${RADII.button};
  padding: ${SPACE.sm} ${SPACE.xl};
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.sm};
  font-weight: ${FONT_WEIGHT.bold};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${COLORS.dangerDeep};
  }
`;

export const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.sm};
  color: ${COLORS.accent};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${SPACE.xs};

  &:hover {
    text-decoration: underline;
  }
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   STEPPER WALLET_CONNECTING
   ═══════════════════════════════════════════════════════════════════════════ */

export const StepperWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE.lg};
  padding: ${SPACE.md} 0;
`;

export const StepRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE.md};
`;

export const StepBadge = styled.div`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: ${RADII.circle};
  background: ${(p) =>
    p.$done ? COLORS.cta
    : p.$active ? COLORS.accent
    : COLORS.line};
  color: ${(p) => (p.$done || p.$active ? COLORS.white : COLORS.muted)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FONT_SIZE.xs};
  font-weight: ${FONT_WEIGHT.bold};
  flex-shrink: 0;
  transition: background 0.2s;
`;

export const StepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

export const StepTitle = styled.span`
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.sm};
  font-weight: ${(p) => (p.$active ? 700 : 400)};
  color: ${(p) => (p.$active ? COLORS.ink : COLORS.muted)};
`;

export const StepSub = styled.span`
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.muted};
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   OTP / KEY DISPLAY
   ═══════════════════════════════════════════════════════════════════════════ */

/** Grille de 6 cases OTP (saisie 1 chiffre par case). */
export const OtpGrid = styled.div`
  display: flex;
  gap: ${SPACE.sm};
  justify-content: center;
`;

export const OtpCell = styled.input`
  width: 2.75rem;
  height: 3.5rem;
  text-align: center;
  font-family: ${FONTS.mono};
  font-size: 1.375rem;
  font-weight: ${FONT_WEIGHT.bold};
  border: 0.0625rem solid ${(p) => (p.$hasError ? COLORS.danger : COLORS.line)};
  border-radius: ${RADII.button};
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 0.1875rem ${COLORS.accentSoft};
  }

  ${media.sm`
    width: 2.25rem;
    height: 3rem;
    font-size: 1.125rem;
  `}
`;

/** Grille 12 mots mnémoniques (3 colonnes × 4). */
export const MnemonicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${SPACE.sm};
  filter: ${(p) => (p.$blurred ? 'blur(0.375rem)' : 'none')};
  transition: filter 0.3s;
  user-select: ${(p) => (p.$blurred ? 'none' : 'text')};
`;

export const MnemonicWord = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE.xs};
  background: ${COLORS.subtle};
  border-radius: ${RADII.button};
  padding: ${SPACE.xs} ${SPACE.sm};
`;

export const MnemonicIndex = styled.span`
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.muted};
  min-width: 1.125rem;
`;

export const MnemonicWordText = styled.span`
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.sm};
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${COLORS.ink};
`;

/* Compte à rebours KEY_DISPLAY */
export const CountdownBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE.xs};
  background: ${COLORS.accentSoft};
  border-radius: ${RADII.pill};
  padding: ${SPACE.xs} ${SPACE.md};
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.sm};
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.accent};
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   ÉTATS SUCCESS / ERROR
   ═══════════════════════════════════════════════════════════════════════════ */

export const StateIconWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE.lg};
  padding: ${SPACE.xxl} 0;
  text-align: center;
`;

export const StateTitle = styled.h4`
  margin: 0;
  font-family: ${FONTS.head};
  font-size: 1.125rem;
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.ink};
`;

export const StateMessage = styled.p`
  margin: 0;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.sm};
  color: ${COLORS.muted};
  line-height: 1.5;
`;

export const AlertBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE.sm};
  padding: ${SPACE.md};
  border-radius: ${RADII.button};
  background: ${(p) =>
    p.$type === 'error'   ? COLORS.dangerSoft
    : p.$type === 'warning' ? COLORS.goldSoft
    : COLORS.accentSoft};
  border: 0.0625rem solid ${(p) =>
    p.$type === 'error'   ? COLORS.danger
    : p.$type === 'warning' ? COLORS.gold
    : COLORS.accent14};
`;

export const AlertText = styled.p`
  margin: 0;
  font-family: ${FONTS.body};
  font-size: 0.8125rem;
  color: ${(p) =>
    p.$type === 'error'   ? COLORS.danger
    : p.$type === 'warning' ? COLORS.warnInk
    : COLORS.accent};
  line-height: 1.5;
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   PICKER V2 (variante A validée) — email-first, wallets compacts en bas
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * PickerBody — body du panel PICKING uniquement.
 * Pas de gap : l'espacement est contrôlé par les margins des éléments enfants
 * (reproduit exactement le .body du preview validé dav2-wallet2-A.html).
 */
export const PickerBody = styled.div`
  flex: 1;
  padding: 1.6rem 1.4rem 1.2rem;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  ${media.sm`
    padding: 1.2rem 1rem 1rem;
  `}
`;

/** Eyebrow lead : "Sign in or create your account" */
export const EyebrowLead = styled.p`
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: ${FONT_SIZE.xs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.muted};
  text-align: center;
  margin: 0 0 1.1rem;
`;

/** Bouton outline accent (border accent, fond blanc) — "Create an account". */
export const OutlineAccentBtn = styled.button`
  width: 100%;
  height: 2.75rem;
  border-radius: ${RADII.button};
  border: 0.09375rem solid rgba(194, 24, 166, 0.4);
  background: ${COLORS.white};
  color: ${COLORS.accent};
  font-family: ${FONTS.head};
  font-size: 0.9375rem;
  font-weight: ${FONT_WEIGHT.bold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACE.sm};
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${COLORS.accentSoft};
    border-color: ${COLORS.accent};
  }

  &:focus-visible {
    outline: 0.125rem solid ${COLORS.accent};
    outline-offset: 0.125rem;
  }
`;

/** Séparateur "or connect a wallet" avec lignes horizontales. */
export const DividerOr = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE.md};
  color: ${COLORS.muted};
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.xs};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 1.4rem 0 1rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 0.0625rem;
    background: ${COLORS.line};
  }
`;

/** Ligne de 4 wallets compacts (horizontale). */
export const WalletMiniRow = styled.div`
  display: flex;
  gap: 0.6rem;
`;

/** Carte wallet compacte (icône + label, flex:1 dans WalletMiniRow). */
export const WalletMiniBtn = styled.button`
  flex: 1;
  border: 0.09375rem solid ${(p) => (p.$mobileAction ? COLORS.accent14 : COLORS.line)};
  border-radius: 0.7rem;
  padding: 0.7rem 0.3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  background: ${COLORS.white};
  transition: border-color 0.15s;
  opacity: ${(p) => (p.disabled ? 0.4 : 1)};

  &:hover:not(:disabled) {
    border-color: ${COLORS.accent};
  }

  &:focus-visible {
    outline: 0.125rem solid ${COLORS.accent};
    outline-offset: 0.125rem;
  }
`;

/** Lien "Install" affiché sous le label wallet quand non injecté sur mobile. */
export const WalletMiniInstallLink = styled.a`
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.accent};
  text-decoration: underline;
  text-underline-offset: 0.1em;
  line-height: 1;

  &:hover {
    color: ${COLORS.accentHover};
  }
`;

/** Icône wallet miniaturisée (1.9rem). */
export const WalletMiniIcon = styled.img`
  width: 1.9rem;
  height: 1.9rem;
  object-fit: contain;
  border-radius: 0.45rem;
  filter: ${(p) => (p.$unavailable ? 'grayscale(1)' : 'none')};
`;

/** Nom wallet en mono minuscule. */
export const WalletMiniLabel = styled.span`
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.muted};
`;

/* Texte ToS en pied de modal */
export const ToSText = styled.p`
  margin: 0;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  font-weight: ${FONT_WEIGHT.normal};
  color: ${COLORS.muted};
  line-height: 1.5;
  text-align: center;

  /* Liens = MÊME typo que la phrase (couleur muted, weight 400, taille héritée) —
     aucune 2e typo (demande Jonathan 2026-06-10). !important pour battre le legacy
     'a' legacy (bleu marine accent2Deep, gras). Seul l'underline
     subtil signale le lien, sans altérer typo/taille/couleur. */
  a {
    color: ${COLORS.muted} !important;
    font-weight: ${FONT_WEIGHT.normal} !important;
    font-size: inherit;
    font-family: inherit;
    text-decoration: underline;
    text-underline-offset: 0.15em;

    &:hover {
      color: ${COLORS.accent} !important;
    }
  }
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   MULTI-WALLET SWITCHER
   ═══════════════════════════════════════════════════════════════════════════ */

export const WalletList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE.sm};
`;

export const WalletListItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${SPACE.md};
  padding: ${SPACE.md};
  border: 0.0625rem solid ${(p) => (p.$isPrimary ? COLORS.accent14 : COLORS.line)};
  border-radius: ${RADII.button};
  background: ${(p) => (p.$isPrimary ? COLORS.accentSoft : COLORS.white)};
`;

export const WalletAddressSpan = styled.span`
  font-family: ${FONTS.mono};
  font-size: 0.8125rem;
  color: ${COLORS.ink};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const WalletTypeBadge = styled.span`
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  font-weight: ${FONT_WEIGHT.bold};
  color: ${(p) => (p.$isPrimary ? COLORS.accent : COLORS.muted)};
  background: ${(p) => (p.$isPrimary ? COLORS.accent14 : COLORS.subtle)};
  padding: 0.125rem ${SPACE.sm};
  border-radius: ${RADII.pill};
  flex-shrink: 0;
`;
